import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Layout } from './components/Layout/Layout';
import { Dashboard } from './components/Dashboard/Dashboard';
import { LessonList } from './components/Lessons/LessonList';
import { LessonForm } from './components/Lessons/LessonForm';
import { LessonSession } from './components/LessonSession/LessonSession';
import { TrackingDashboardPage } from './components/Tracking/TrackingDashboardPage';
import { SchedulePage } from './components/Schedule';
import { AuthPage } from './components/Auth';
import { LessonLibrary } from './components/Lessons/LessonLibrary';
import { AdminLessonManager } from './components/Admin/AdminLessonManager';
import { StudentManagement } from './components/Students/StudentManagement';
import { LessonProvider } from './contexts/LessonContext';
import { TrackingProvider } from './contexts/TrackingContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './index.css';
import { GPFResourceDemo } from './components/ResourceComponents/GPFResourceDemo';



// App Routes Component
const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {user ? (
        <Layout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/lessons" element={<LessonList />} />
            <Route path="/lessons/library" element={<LessonLibrary />} />
            <Route path="/lessons/new" element={<LessonForm mode="create" />} />
            <Route path="/admin/lessons" element={<AdminLessonManager />} />
            <Route path="/students" element={<StudentManagement />} />
            <Route path="/lessons/:lessonId/edit" element={<LessonForm mode="edit" />} />
            <Route path="/session/:lessonId" element={<LessonSession />} />
            <Route path="/tracking" element={<TrackingDashboardPage />} />
            <Route path="/tracking/:lessonId" element={<TrackingDashboardPage />} />
            <Route path="/schedule" element={<SchedulePage teacherId={user.id} />} />
            <Route path="/gpf-resources" element={<GPFResourceDemo />} />
            <Route path="/analytics" element={<div className="card"><h1>Analytics</h1><p>Coming soon...</p></div>} />
            <Route path="/settings" element={<div className="card"><h1>Settings</h1><p>Coming soon...</p></div>} />
            <Route path="*" element={<div className="card"><h1>404 - Page Not Found</h1><p>The page you're looking for doesn't exist.</p></div>} />
          </Routes>
        </Layout>
      ) : (
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="*" element={<Navigate to="/auth" replace />} />
        </Routes>
      )}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <TranslationProvider>
      <AuthProvider>
        <LessonProvider>
          <TrackingProvider>
            <Router>
              <AppRoutes />
            </Router>
          </TrackingProvider>
        </LessonProvider>
      </AuthProvider>
    </TranslationProvider>
  );
}

export default App;
